import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/Dialog";
import Button from "@/components/ui/Button";
import {
  AssetView,
  AssetCondition,
  ReturnReason,
  ReturnAssetInput,
} from "../../../../api/General/assets";

interface ReturnAssetModalProps {
  open: boolean;
  onClose: () => void;
  asset: AssetView | null;
  onReturn: (input: ReturnAssetInput) => Promise<void>;
  returning: boolean;
}

const ReturnAssetModal: React.FC<ReturnAssetModalProps> = ({
  open,
  onClose,
  asset,
  onReturn,
  returning,
}) => {
  const { t } = useTranslation();

  const [returnDate, setReturnDate] = useState(new Date().toISOString().split("T")[0]);
  const [condition, setCondition] = useState<AssetCondition>(AssetCondition.Good);
  const [returnReason, setReturnReason] = useState<ReturnReason | "">("");
  const [hasDamage, setHasDamage] = useState(false);
  const [damageDescription, setDamageDescription] = useState("");
  const [repairRequired, setRepairRequired] = useState(false);
  const [repairCost, setRepairCost] = useState("");
  const [receivedBy, setReceivedBy] = useState("");
  const [digitalSignature, setDigitalSignature] = useState("");
  const [notes, setNotes] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset on open
  useEffect(() => {
    if (open && asset) {
      setReturnDate(new Date().toISOString().split("T")[0]);
      setCondition(asset.condition);
      setReturnReason("");
      setHasDamage(false);
      setDamageDescription("");
      setRepairRequired(false);
      setRepairCost("");
      setReceivedBy("");
      setDigitalSignature("");
      setNotes("");
      setErrors({});
    }
  }, [open, asset]);

  // Clear damage fields when unchecked
  useEffect(() => {
    if (!hasDamage) {
      setDamageDescription("");
      setRepairRequired(false);
      setRepairCost("");
    }
  }, [hasDamage]);

  const conditionLabel = (c: AssetCondition) => {
    switch (c) {
      case AssetCondition.Excellent: return t("assets.conditionExcellent");
      case AssetCondition.Good: return t("assets.conditionGood");
      case AssetCondition.Fair: return t("assets.conditionFair");
      case AssetCondition.Poor: return t("assets.conditionPoor");
      default: return "";
    }
  };

  const returnReasonLabel = (r: ReturnReason) => {
    switch (r) {
      case ReturnReason.EmployeeTermination: return t("assets.returnReasonTermination");
      case ReturnReason.AssetReplacement: return t("assets.returnReasonReplacement");
      case ReturnReason.TemporaryAssignmentEnded: return t("assets.returnReasonTempEnded");
      case ReturnReason.AssetNoLongerNeeded: return t("assets.returnReasonNotNeeded");
      case ReturnReason.RepairMaintenanceRequired: return t("assets.returnReasonRepair");
      case ReturnReason.Other: return t("assets.returnReasonOther");
      default: return "";
    }
  };

  const conditionColorClass = (c: AssetCondition) => {
    switch (c) {
      case AssetCondition.Excellent: return "text-success";
      case AssetCondition.Good: return "text-primary";
      case AssetCondition.Fair: return "text-warning";
      case AssetCondition.Poor: return "text-destructive";
      default: return "text-muted-foreground";
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!returnDate) newErrors.returnDate = t("assets.validation.returnDateRequired");
    if (returnReason === "") newErrors.returnReason = t("assets.validation.returnReasonRequired");
    if (hasDamage && !damageDescription.trim())
      newErrors.damageDescription = t("assets.validation.damageDescRequired");
    if (repairRequired && (!repairCost || isNaN(parseFloat(repairCost))))
      newErrors.repairCost = t("assets.validation.repairCostRequired");
    if (!receivedBy.trim()) newErrors.receivedBy = t("assets.validation.receivedByRequired");
    if (!digitalSignature.trim()) newErrors.digitalSignature = t("assets.validation.signatureRequired");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !asset || returnReason === "") return;

    await onReturn({
      assetId: asset.id,
      returnDate,
      condition,
      returnReason: returnReason as ReturnReason,
      hasDamage,
      damageDescription: hasDamage ? damageDescription : undefined,
      repairRequired,
      repairCost: repairRequired ? parseFloat(repairCost) : undefined,
      receivedBy,
      digitalSignature,
      notes: notes || undefined,
    });
  };

  if (!asset) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !returning) onClose(); }}>
      <DialogContent size="lg">
        <DialogHeader className="sticky top-0 bg-card z-10">
          <DialogTitle>{t("assets.returnAsset")}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <DialogBody className="space-y-6">
            {/* Asset Info */}
            <div className="bg-muted rounded-lg p-4">
              <h3 className="text-sm font-medium text-foreground mb-3">{t("assets.assetInformation")}</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">{t("assets.name")}</span>
                  <p className="font-medium">{asset.name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">{t("assets.serialNumber")}</span>
                  <p className="font-medium font-mono">{asset.serialNumber || "-"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">{t("assets.currentlyIssuedTo")}</span>
                  <p className="font-medium">{asset.assignedToUserName || "-"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">{t("assets.category")}</span>
                  <p className="font-medium">{asset.categoryName}</p>
                </div>
              </div>
            </div>

            {/* Return Details */}
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3">{t("assets.returnDetails")}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t("assets.returnDate")} *
                  </label>
                  <input
                    type="date"
                    value={returnDate}
                    onChange={(e) => setReturnDate(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ${
                      errors.returnDate ? "border-destructive" : "border-border"
                    }`}
                    disabled={returning}
                    data-testid="return-date-input"
                  />
                  {errors.returnDate && <p className="mt-1 text-xs text-destructive">{errors.returnDate}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t("assets.currentCondition")} *
                  </label>
                  <select
                    value={condition}
                    onChange={(e) => setCondition(e.target.value as AssetCondition)}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    disabled={returning}
                    data-testid="return-condition-select"
                  >
                    {[AssetCondition.Excellent, AssetCondition.Good, AssetCondition.Fair, AssetCondition.Poor].map((c) => (
                      <option key={c} value={c}>{conditionLabel(c)}</option>
                    ))}
                  </select>
                  <p className={`mt-1 text-xs ${conditionColorClass(condition)}`}>
                    {t("assets.currentCondition")}: {conditionLabel(condition)}
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t("assets.returnReason")} *
                </label>
                <select
                  value={returnReason}
                  onChange={(e) => setReturnReason(e.target.value === "" ? "" : e.target.value as ReturnReason)}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ${
                    errors.returnReason ? "border-destructive" : "border-border"
                  }`}
                  disabled={returning}
                  data-testid="return-reason-select"
                >
                  <option value="">{t("assets.selectReturnReason")}</option>
                  {[
                    ReturnReason.EmployeeTermination,
                    ReturnReason.AssetReplacement,
                    ReturnReason.TemporaryAssignmentEnded,
                    ReturnReason.AssetNoLongerNeeded,
                    ReturnReason.RepairMaintenanceRequired,
                    ReturnReason.Other,
                  ].map((r) => (
                    <option key={r} value={r}>{returnReasonLabel(r)}</option>
                  ))}
                </select>
                {errors.returnReason && <p className="mt-1 text-xs text-destructive">{errors.returnReason}</p>}
              </div>
            </div>

            {/* Damage Assessment */}
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3">{t("assets.damageAssessment")}</h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasDamage}
                  onChange={(e) => setHasDamage(e.target.checked)}
                  className="rounded border-border text-destructive focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  disabled={returning}
                  data-testid="has-damage-checkbox"
                />
                <span className="text-sm text-foreground">{t("assets.hasDamage")}</span>
              </label>

              {hasDamage && (
                <div className="mt-3 space-y-3 bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      {t("assets.damageDescription")} *
                    </label>
                    <textarea
                      value={damageDescription}
                      onChange={(e) => setDamageDescription(e.target.value)}
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ${
                        errors.damageDescription ? "border-destructive" : "border-border"
                      }`}
                      placeholder={t("assets.damageDescPlaceholder")}
                      disabled={returning}
                      data-testid="damage-description-input"
                    />
                    {errors.damageDescription && <p className="mt-1 text-xs text-destructive">{errors.damageDescription}</p>}
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={repairRequired}
                      onChange={(e) => setRepairRequired(e.target.checked)}
                      className="rounded border-border text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      disabled={returning}
                      data-testid="repair-required-checkbox"
                    />
                    <span className="text-sm text-foreground">{t("assets.repairRequired")}</span>
                  </label>

                  {repairRequired && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        {t("assets.repairCost")} *
                      </label>
                      <input
                        type="number"
                        value={repairCost}
                        onChange={(e) => setRepairCost(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ${
                          errors.repairCost ? "border-destructive" : "border-border"
                        }`}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        disabled={returning}
                        data-testid="repair-cost-input"
                      />
                      {errors.repairCost && <p className="mt-1 text-xs text-destructive">{errors.repairCost}</p>}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Verification */}
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3">{t("assets.returnVerification")}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t("assets.returnedBy")}
                  </label>
                  <input
                    type="text"
                    value={asset.assignedToUserName || ""}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-muted"
                    disabled
                  />
                  <p className="mt-1 text-xs text-muted-foreground">{t("assets.employeeReturning")}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t("assets.receivedBy")} *
                  </label>
                  <input
                    type="text"
                    value={receivedBy}
                    onChange={(e) => setReceivedBy(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ${
                      errors.receivedBy ? "border-destructive" : "border-border"
                    }`}
                    placeholder={t("assets.receivedByPlaceholder")}
                    disabled={returning}
                    data-testid="received-by-input"
                  />
                  {errors.receivedBy && <p className="mt-1 text-xs text-destructive">{errors.receivedBy}</p>}
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t("assets.additionalNotes")}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  placeholder={t("assets.returnNotesPlaceholder")}
                  disabled={returning}
                  data-testid="return-notes-input"
                />
              </div>
            </div>

            {/* Digital Signature */}
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3">{t("assets.digitalAcknowledgment")}</h3>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t("assets.digitalSignature")} *
                </label>
                <input
                  type="text"
                  value={digitalSignature}
                  onChange={(e) => setDigitalSignature(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg text-sm italic focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ${
                    errors.digitalSignature ? "border-destructive" : "border-border"
                  }`}
                  placeholder={t("assets.signaturePlaceholder")}
                  disabled={returning}
                  data-testid="digital-signature-input"
                />
                {errors.digitalSignature && <p className="mt-1 text-xs text-destructive">{errors.digitalSignature}</p>}
                <p className="mt-1 text-xs text-muted-foreground">{t("assets.signatureHint")}</p>
              </div>
            </div>
          </DialogBody>

          {/* Actions */}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={returning}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              loading={returning}
              data-testid="return-asset-submit-btn"
            >
              {returning ? t("assets.processingReturn") : (
                <>
                  <svg className="w-4 h-4 me-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                  {t("assets.processReturn")}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReturnAssetModal;
